import React, { createContext, ReactNode, useEffect, useState } from 'react';
import useContext from '../../hooks/useContext';
import { styled } from 'styled-components';
import { Text } from '../../styled/Text/wrappers';
import { size } from '../../styled/layout/helpers';

interface IReviewProvider {
  children: ReactNode;
  interactWithReviewBadge: boolean;
}

interface ReviewContext {
  interactWithReviewBadge: boolean;
}

interface IReviewComponent {
  moderArtikelId?: string;
  groupId: string;
}

interface IBadgeButton {
  onClick: () => void;
  handleReviews: (shouldShowReviews: boolean) => void;
}

const Wrapper = styled.div`
  margin-top: ${size(1)};
  margin-bottom: ${size(4)};
`;

export default function ReviewsProductPage(component: IReviewComponent) {
  const familyId = getFamilyId(component);

  window.testFreaks = window.testFreaks || [];
  window.testFreaks.push(['reset']);
  window.testFreaks.push(['setFamilyId', familyId]);
  window.testFreaks.push(['load', ['reviews']]);

  return (
    <>
      <Wrapper>
        <Text className="size-small">
          Vi visar omdömen oavsett betyg och säkerställer att alla recensioner uteslutande skrivs av
          kunder som har köpt varan. Det görs med hjälp av Trustvoice som administrerar betyg och
          recensioner.
        </Text>
      </Wrapper>
      <div id="testfreaks-reviews"></div>
    </>
  );
}

export const reviewListing = () => {
  window.testFreaks = window.testFreaks || [];
  window.testFreaks.push(['load', 'items']);
};

export const loadBadge = (component: IReviewComponent) => {
  const familyId = getFamilyId(component);

  removeOldBadge();
  window.testFreaks = window.testFreaks || [];
  window.testFreaks.push(['reset']);
  window.testFreaks.push(['setFamilyId', familyId]);
  window.testFreaks.push(['setAggregateRatingSchema', 'json-ld']);
  window.testFreaks.push(['load', ['badge']]);
};

const removeOldBadge = () => {
  const oldBadge = document.getElementById('testfreaks-badge');
  if (oldBadge) {
    oldBadge.innerHTML = '';
  }
};

const ReviewContext = createContext<ReviewContext | undefined>(undefined);

export const ReviewProvider = ({ children, interactWithReviewBadge }: IReviewProvider) => {
  return (
    <ReviewContext.Provider value={{ interactWithReviewBadge }}>{children}</ReviewContext.Provider>
  );
};

export const useReviewContext = () => {
  const context = useContext(ReviewContext);
  return context || null;
};

export const BadgeButton = ({ onClick, handleReviews }: IBadgeButton) => {
  const [childCount, setChildCount] = useState(0);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (buttonRef.current) {
        setChildCount(buttonRef.current.children.length);
      }
    });

    if (buttonRef.current) {
      observer.observe(buttonRef.current, { childList: true });
      setChildCount(buttonRef.current.children.length);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    handleReviews(!!childCount);
  }, [childCount]);

  return (
    <button
      id="testfreaks-badge"
      aria-label="Öppna panel för recensioner"
      aria-haspopup="dialog"
      onClick={onClick}
      ref={buttonRef}></button>
  );
};

export const TestFreaksItem = (component: IReviewComponent) => {
  const familyId = getFamilyId(component);

  return <div className="testfreaks-item" data-family-id={familyId} />;
};

/**
 * Get the family id for the review component
 * @param component - The review component
 * @returns The family id, prioritizing moderArtikelId over groupId
 */
const getFamilyId = (component: IReviewComponent) => component.moderArtikelId ?? component.groupId;
